import workspaceConfig from '@/configs/permissions/workspace-capabilities.json';
import {
  CAPABILITY_WORKSPACE_VALUES,
  getCapabilities,
  isCapabilityApproval,
  isCapabilityKey,
  type CapabilityApproval,
  type CapabilityWorkspace,
} from './capabilities';

// F5 — the workspace capability scheme (PROP-01 §4 Layer A, PROP-02 F5).
//
// `capabilities.json` compiles the legacy permission booleans into domain
// capabilities (`catalog.menus.manage`, `pos.floor.access`). That is the right
// shape for a code permission check but the wrong shape for a person: a waiter
// does not hold "catalog.menus.manage", they hold "change a price".
//
// F5 reshapes those into one access level per workspace — `<section>.<level>` —
// which is the shape `lib/routing/route-table.ts` already declares in its
// `capability` column. The two registries coexist and union:
//
//   registry   legacy key -> EXACTLY ONE capability   (T-1.1, unchanged)
//   this file  legacy key -> a SET of capabilities     (F5's `manage_branches`
//                                                          deliberately splits)
//
// The T-1.1 rule is why this is a separate file. A legacy key cannot map to two
// capabilities in the registry, and F5 maps one legacy key to five, so the split
// cannot live there. This file claims only the keys that CONVERGE; keys that stay
// in their own layer are listed in `passThrough`, which names the existing
// capability without claiming the key, so the registry still owns it and T-1.1
// still holds across both files.
//
// Naming note: `section` here is a PRODUCT AREA (orders, menu, catering). It is
// not the `workspace` field on a `CapabilityDefinition`, which is a data SCOPE
// (business | branch). Same word, two meanings, so the scope field on a workspace
// capability is called `dataScope` and carries the same meaning as `workspace`.

export type WorkspaceLevel = 'view' | 'edit' | 'manage';

export interface WorkspaceCapabilityDefinition {
  key: string;
  /** Product area: orders, menu, catering, … Not a data scope. */
  section: string;
  /** view | edit | manage, or a settings/business sub-key. */
  level: string;
  label: string;
  description: string;
  approval: CapabilityApproval;
  /** business | branch. Same meaning as `CapabilityDefinition.workspace`. */
  dataScope: CapabilityWorkspace;
  legacyPermissionKeys: string[];
  branchEligibility: string[];
}

export type WorkspacePassThrough = {
  /** The existing capability this legacy key keeps. */
  capability: string;
  note: string;
};

export type WorkspaceKnownGap = {
  key: string;
  issue: string;
  resolution: string;
  legacyKeys?: string[];
};

interface WorkspaceCapabilitiesConfig {
  _generated: string;
  levelOrder: WorkspaceLevel[];
  /**
   * Workspace-scheme keys the REGISTRY owns — `inventory.manage`,
   * `business.delete`. They are part of the scheme, so level implication applies
   * to them, but they are not redeclared here: the registry's own definition and
   * `branchEligibility` stay authoritative.
   */
  existingSchemeKeys: string[];
  sections: Record<string, { label: string; description: string; levels: string[] }>;
  extraSections: Record<string, { label: string; description: string; subKeys: string[] }>;

  ownerOnly: string[];
  capabilities: WorkspaceCapabilityDefinition[];
  passThrough: Record<string, WorkspacePassThrough>;
  knownGaps: WorkspaceKnownGap[];
}

const config = workspaceConfig as unknown as WorkspaceCapabilitiesConfig;

export const WORKSPACE_LEVEL_ORDER: readonly WorkspaceLevel[] = config.levelOrder;

/** The three capabilities F5 says cannot be delegated to a policy at all. */
export const WORKSPACE_OWNER_ONLY: readonly string[] = config.ownerOnly;

/**
 * Workspace-scheme keys the registry owns. Part of the scheme, so level
 * implication applies, but not redeclared here.
 */
export const WORKSPACE_EXISTING_SCHEME_KEYS: readonly string[] = config.existingSchemeKeys;

export function getWorkspaceCapabilities(): WorkspaceCapabilityDefinition[] {
  return [...config.capabilities];
}

export function getWorkspaceCapability(key: string): WorkspaceCapabilityDefinition | undefined {
  return config.capabilities.find((c) => c.key === key);
}

export function getWorkspaceCapabilityKeys(): string[] {
  return config.capabilities.map((c) => c.key);
}

export function isWorkspaceCapabilityKey(input: unknown): input is string {
  return typeof input === 'string' && config.capabilities.some((c) => c.key === input);
}

/** Product area of a workspace capability, or undefined if the key is not one. */
export function workspaceSectionOf(key: string): string | undefined {
  return getWorkspaceCapability(key)?.section;
}

export function workspaceLevelOf(key: string): string | undefined {
  return getWorkspaceCapability(key)?.level;
}

/** The sections F5 declares, with the levels each one actually has. */
export function getWorkspaceSections(): typeof config.sections {
  return config.sections;
}

export function getWorkspaceKnownGaps(): WorkspaceKnownGap[] {
  return [...config.knownGaps];
}

// ─── The compatibility layer ──────────────────────────────────────────────────

export type WorkspaceCompatLayer = 'workspace' | 'existing';

export type WorkspaceCompatResolution = {
  legacyKey: string;
  /**
   * `workspace` — the key converged onto F5's `<section>.<level>` scheme.
   * `existing` — the key stays in its own layer (POS, per-person AI) and keeps
   * the capability the registry already gave it. Not a failure: it is the
   * documented end state for those layers, and the reason the mapping cannot be
   * a single flat `legacy -> capability` object.
   */
  layer: WorkspaceCompatLayer;
  /** Direct targets, before level implication. */
  capabilities: string[];
  note: string;
};

/**
 * The forward direction: a legacy permission key → the capabilities it now means.
 *
 * Every one of the 43 keys in `roles.json` resolves. Keys that converge return
 * their F5 targets; keys in the POS and per-person-AI layers return the existing
 * capability they already had, so every check written against the old registry
 * keeps working untouched.
 */
export function resolveLegacyPermissionKey(legacyKey: string): WorkspaceCompatResolution | undefined {
  const capability = config.capabilities.find((c) => c.legacyPermissionKeys.includes(legacyKey));
  if (capability) {
    return {
      legacyKey,
      layer: 'workspace',
      capabilities: config.capabilities
        .filter((c) => c.legacyPermissionKeys.includes(legacyKey))
        .map((c) => c.key),
      note: 'Converged on the workspace scheme.',
    };
  }

  const passThrough = config.passThrough[legacyKey];
  if (passThrough) {
    return {
      legacyKey,
      layer: 'existing',
      capabilities: [passThrough.capability],
      note: passThrough.note,
    };
  }

  return undefined;
}

/** The reverse direction: a capability → the legacy permission keys that grant it. */
/**
 * The legacy keys that grant `key`, from BOTH layers.
 *
 * A capability can be reached two ways: declared as a workspace capability
 * (`legacyPermissionKeys`) or as a pass-through pointing at a registry
 * capability. These are not alternatives — they are additive, and returning only
 * the workspace entry silently dropped pass-through keys. P5-02 hit exactly that:
 * `manage_service_console` is a pass-through to `pos.settings.manage`, which is
 * also declared as a workspace capability, so the reverse map reported only
 * `manage_pos_settings` and the key appeared to grant nothing in reverse.
 */
export function legacyKeysForCapability(key: string): string[] {
  const legacyKeys = new Set<string>();

  for (const legacyKey of getWorkspaceCapability(key)?.legacyPermissionKeys ?? []) {
    legacyKeys.add(legacyKey);
  }

  for (const legacyKey of getPassThroughTargets(key)) {
    legacyKeys.add(legacyKey);
  }

  const registryEntry = getCapabilities().find((c) => c.key === key);
  for (const legacyKey of registryEntry?.legacyPermissionKeys ?? []) {
    legacyKeys.add(legacyKey);
  }

  return [...legacyKeys];
}

/** The legacy keys declared as pass-throughs that target `capabilityKey`. */
function getPassThroughTargets(capabilityKey: string): string[] {
  return Object.entries(config.passThrough)
    .filter(([, entry]) => entry.capability === capabilityKey)
    .map(([legacyKey]) => legacyKey);
}

/**
 * The effective set a member ends up with, given the legacy permission keys on
 * their role row: every target of every key, plus the levels below each target.
 *
 * The implication is not optional. F5's table maps `refund_orders` to
 * `orders.edit` and nothing else, which would leave a role able to refund an
 * order it cannot open. `view < edit < manage` is PROP-01 §4's own ordering, so
 * `orders.edit` implies `orders.view`.
 *
 * Settings and business sub-keys are not levels, so they are passed through
 * untouched.
 */
export function effectiveCapabilitiesForLegacyKeys(legacyKeys: readonly string[]): string[] {
  const effective = new Set<string>();
  for (const legacyKey of legacyKeys) {
    const resolution = resolveLegacyPermissionKey(legacyKey);
    if (!resolution) continue;
    for (const key of expandWithImpliedLevels(resolution.capabilities)) {
      effective.add(key);
    }
  }
  return [...effective].sort();
}

/**
 * `[section.manage] -> [section.edit, section.manage, section.view]`.
 *
 * Applies to workspace-registry keys AND to `existingSchemeKeys`, so a role that
 * holds the registry's own `inventory.manage` still gains `inventory.view`. It
 * deliberately does NOT parse arbitrary keys: `team.members.manage` ends in
 * "manage", but its section is `team` only by coincidence, and implying
 * `team.view` from it would be a grant nobody asked for.
 */
export function expandWithImpliedLevels(capabilityKeys: readonly string[]): string[] {
  const expanded = new Set<string>();
  for (const key of capabilityKeys) {
    expanded.add(key);
    const definition = getWorkspaceCapability(key);
    const inScheme = definition !== undefined || config.existingSchemeKeys.includes(key);
    if (!inScheme) continue;

    const section = definition?.section ?? key.slice(0, key.lastIndexOf('.'));
    const level = definition?.level ?? key.slice(key.lastIndexOf('.') + 1);
    const sectionLevels = config.sections[section]?.levels ?? [];
    const at = sectionLevels.indexOf(level);
    if (at < 0) continue;

    for (const implied of sectionLevels.slice(0, at + 1)) {
      if (!config.levelOrder.includes(implied as WorkspaceLevel)) continue;
      const impliedKey = `${section}.${implied}`;
      // Only ever imply keys that really exist.
      if (isWorkspaceCapabilityKey(impliedKey) || config.existingSchemeKeys.includes(impliedKey)) {
        expanded.add(impliedKey);
      }
    }
  }
  return [...expanded].sort();
}

// ─── Validation ───────────────────────────────────────────────────────────────

/**
 * Every declared section with its levels: the `<section>.<level>` ones plus the
 * settings/business/ai sub-keys, which are declared in a separate block because
 * they are not access levels and must not be implied.
 */
export type DeclaredSections = Record<string, { levels: string[] }>;

export function declaredSections(): DeclaredSections {
  const declared: DeclaredSections = {};
  for (const [section, def] of Object.entries(config.sections)) {
    declared[section] = { levels: [...def.levels] };
  }
  for (const [section, def] of Object.entries(config.extraSections)) {
    declared[section] = { levels: [...def.subKeys] };
  }
  return declared;
}

/**
 * The registry and the workspace scheme as one list, in `CapabilityDefinition`
 * shape so policy validation can check either kind of grant with the same code.
 *
 * This is the union a policy is validated against — NOT what
 * `getCapabilities()` returns. That stays the 44-capability T-1.1 registry on
 * purpose: `validateCapabilityRegistry` requires every capability to own at least
 * one legacy key, which is true of the registry and false of the 23 workspace
 * capabilities that only an owner or admin can grant.
 */
export function getEffectiveCapabilities(): Array<{
  key: string;
  label: string;
  description: string;
  legacyPermissionKeys: string[];
  workspace: CapabilityWorkspace;
  approval: CapabilityApproval;
  branchEligibility: string[];
  retired?: boolean;
}> {
  return [...getCapabilities(), ...config.capabilities.map((c) => ({
    key: c.key,
    label: c.label,
    description: c.description,
    legacyPermissionKeys: c.legacyPermissionKeys,
    workspace: c.dataScope,
    approval: c.approval,
    branchEligibility: c.branchEligibility,
  }))];
}

export { config as workspaceCapabilitiesConfig };

/**
 * Whether `input` is a capability key in EITHER registry — the 44 domain
 * capabilities or the 42 workspace ones.
 *
 * `isCapabilityKey` in `./capabilities` is deliberately registry-only, because
 * T-1.1 is a statement about `capabilities.json` alone. That made it the wrong
 * predicate the moment F5 added a second vocabulary: a gate naming
 * `locations.manage` was being tested against a registry that does not contain
 * it, and silently answered `false`. Callers that mean "is this a capability at
 * all" want this one; callers inside the T-1.1 validator still want the other.
 */
export function isEffectiveCapabilityKey(input: unknown): input is string {
  return (
    typeof input === 'string' &&
    (isCapabilityKey(input) || isWorkspaceCapabilityKey(input))
  );
}
